import { useRef, useState } from 'react';
import { FlowUser } from './flowApi';

function initials(name?: string) {
  if (!name) return '?';
  const p = name.trim().split(' ');
  return (p[0][0] + (p[1]?.[0] || '')).toUpperCase();
}

// Resolve os @ do texto para IDs de usuários (compara pelo nome completo inserido)
export function resolveMentions(text: string, users: FlowUser[]): string[] {
  const lower = text.toLowerCase();
  return users.filter(u => u.nome && lower.includes('@' + u.nome.toLowerCase())).map(u => u.id);
}

interface MentionFieldProps {
  value: string;
  onChange: (v: string) => void;
  users: FlowUser[];
  placeholder?: string;
  onSubmit?: () => void;
  multiline?: boolean;
  className?: string;
  autoFocus?: boolean;
}

// Campo de texto com autocomplete de menções: digite @ para escolher uma conta
export function MentionField({ value, onChange, users, placeholder, onSubmit, multiline, className, autoFocus }: MentionFieldProps) {
  const [query, setQuery] = useState<string | null>(null);
  const [caret, setCaret] = useState(0);
  const [active, setActive] = useState(0);
  const ref = useRef<any>(null);

  const detect = (text: string, pos: number) => {
    const upto = text.slice(0, pos);
    const m = upto.match(/(^|\s)@([^\s@]*)$/);
    if (m) { setQuery(m[2]); setCaret(pos); setActive(0); }
    else setQuery(null);
  };

  const filtered = query !== null
    ? users.filter(u => u.nome.toLowerCase().includes(query.toLowerCase())).slice(0, 6)
    : [];

  const pick = (u: FlowUser) => {
    const upto = value.slice(0, caret);
    const at = upto.lastIndexOf('@');
    if (at < 0) return;
    const next = value.slice(0, at) + '@' + u.nome + ' ' + value.slice(caret);
    onChange(next);
    setQuery(null);
    setTimeout(() => ref.current?.focus(), 0);
  };

  const handleChange = (e: any) => {
    onChange(e.target.value);
    detect(e.target.value, e.target.selectionStart ?? e.target.value.length);
  };

  const handleKeyDown = (e: any) => {
    if (query !== null && filtered.length) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(a + 1, filtered.length - 1)); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pick(filtered[active]); return; }
      if (e.key === 'Escape') { setQuery(null); return; }
    }
    if (e.key === 'Enter' && onSubmit) {
      if (multiline && e.shiftKey) return; // Shift+Enter quebra linha
      e.preventDefault();
      onSubmit();
    }
  };

  const shared: any = {
    ref,
    value,
    placeholder,
    autoFocus,
    onChange: handleChange,
    onKeyDown: handleKeyDown,
    className,
  };

  return (
    <div className="relative flex-1">
      {query !== null && filtered.length > 0 && (
        <div className="absolute bottom-full left-0 z-[1400] mb-1 w-60 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl">
          {filtered.map((u, i) => (
            <button key={u.id} type="button" onMouseDown={(e) => { e.preventDefault(); pick(u); }}
              className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm ${i === active ? 'bg-cyan-50 text-cyan-800' : 'text-slate-700 hover:bg-slate-50'}`}>
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-cyan-100 text-[10px] font-bold text-cyan-700">{initials(u.nome)}</span>
              <span className="truncate">{u.nome}</span>
            </button>
          ))}
        </div>
      )}
      {multiline
        ? <textarea {...shared} rows={1} />
        : <input {...shared} type="text" />}
    </div>
  );
}
